import NotAvailablePlaceholder from 'loyalty/components/NotAvailablePlaceholder'
import { FormattedMessage, useIntl } from 'react-intl'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import BodyText from 'LuxKit/Typography/BodyText'
import Table from 'LuxKit/Tables/Table'
import TableHeader from 'LuxKit/Tables/TableHeader'
import TableHeaderRow from 'LuxKit/Tables/TableHeaderRow'
import TableHeaderCell from 'LuxKit/Tables/TableHeaderCell'
import TableBody from 'LuxKit/Tables/TableBody'
import TableBodyCell from 'LuxKit/Tables/TableBodyCell'
import TableBodyRow from 'LuxKit/Tables/TableBodyRow'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import Colour from 'components/utils/Colour'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'

interface Props {
  offerName: string
  quantity: number
  pointsCalculation?: Omit<App.LoyaltyPointsEarnCalculation, 'request'>
}

function MerchandisePointsPayCalculatorInfoModal({
  offerName,
  quantity,
  pointsCalculation,
}: Props) {
  const intl = useIntl()
  const programName = getLoyaltyProgramDisplayName()

  const earnableBasePoints = pointsCalculation?.basePoints ?? 0
  const earnableBonusPoints = pointsCalculation?.bonusPoints ?? 0
  const earnableStatusCredits = pointsCalculation?.statusCredits ?? 0

  const hasAnyBonusPoints = earnableBonusPoints > 0

  const offerNameLabel = quantity > 1
    ? intl.formatMessage(
        {
          id: '9-Xr89FvSsAkqMn1lXeQ',
          defaultMessage: '{offerName} (x{quantity, number})',
          description: 'Offer name with quantity multiplier in parentheses, e.g. Product (x3)',
        },
        { offerName, quantity },
      )
    : offerName

  return <ModalBase dismissible size="S">
    <ModalHeader title={intl.formatMessage({
      id: 'yxuAxguIA2f0zaxZ6Qv6',
      defaultMessage: 'Earning points',
      description: 'Modal title for points earning breakdown dialog window',
    })}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={16}>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '9AH-ydT4gw_RBpKTCUrA',
              defaultMessage: 'Below is a breakdown of the points earned for this product.',
              description: 'Description text for points earning breakdown modal',
            })}
          </BodyText>

          <Table>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell width={288}>
                  {intl.formatMessage({
                    id: 'hDt0tdu9xWCdRQcaZszf',
                    defaultMessage: 'Product',
                    description: 'Table column header for product name in the merchandise shop section',
                  })}
                </TableHeaderCell>
                <TableHeaderCell>
                  {intl.formatMessage({
                    id: 'cLjQP6ts6IGmWyuWJ9AZ',
                    defaultMessage: 'Points',
                    description: 'Table column header for base points in the loyalty program section',
                  })}
                </TableHeaderCell>
                {hasAnyBonusPoints && <TableHeaderCell minWidth={92}>
                  <Colour value="highlight-primary-dark">
                    {intl.formatMessage({
                      id: 'py4INL72PfJMNaF9Zoap',
                      defaultMessage: 'Bonus Points',
                      description: 'Table column header for bonus points in the loyalty program section',
                    })}
                  </Colour>
                </TableHeaderCell>}
                <TableHeaderCell minWidth={106}>
                  {intl.formatMessage({
                    id: 'd7PLfNmuCrXNz46-UnoK',
                    defaultMessage: 'Status Credits',
                    description: 'Table column header for status credits in the merchandise shop section',
                  })}
                </TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              <TableBodyRow>
                <TableBodyCell>
                  <BodyTextBlock
                    variant="medium"
                    colour="light-foreground-alt"
                    startIcon={<LineShoppingBagIcon />}
                    format="propercase"
                  >
                    {offerNameLabel}
                  </BodyTextBlock>
                </TableBodyCell>
                <TableBodyCell>
                  <BodyText variant="medium" colour="light-foreground-alt">
                    {earnableBasePoints ? <FormatDecimal value={earnableBasePoints} /> : <NotAvailablePlaceholder />}
                  </BodyText>
                </TableBodyCell>
                {hasAnyBonusPoints && <TableBodyCell>
                  <BodyText variant="medium">
                    <Colour value="highlight-primary-dark">
                      <FormatDecimal value={earnableBonusPoints} />
                    </Colour>
                  </BodyText>
                </TableBodyCell>}
                <TableBodyCell>
                  <BodyText variant="medium" colour="light-foreground-alt">
                    {earnableStatusCredits ? <FormatDecimal value={earnableStatusCredits} /> : <NotAvailablePlaceholder />}
                  </BodyText>
                </TableBodyCell>
              </TableBodyRow>
            </TableBody>
          </Table>

          <BodyText variant="small" colour="light-foreground-alt">
            {intl.formatMessage({
              id: 'zNASRYG8gZ81VpZHzchr',
              defaultMessage: 'Please note that the final points balance will be credited to your account within 30-40 days after your Shop order is confirmed, and may be different from what you see here.',
              description: 'Disclaimer about points credit timing in the loyalty program section',
            })}
            <br /><br />
            <FormattedMessage
              id="MCkAcA3Rw7ExdXFMhVkv"
              defaultMessage="You will not earn points on any portion or whole item that is paid for with points. Points can only be earned on the remaining dollar value. Some payments types are not eligible for earning points such as promotional codes, credits received before {programName} launched, credits received due to cancellation of a booking before {programName} launched."
              description="Disclaimer about points earning restrictions in the booking flow"
              values={{ programName }}
            />
          </BodyText>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </ModalBase>
}

export default MerchandisePointsPayCalculatorInfoModal
